/**
 * Health ▸ Lookup — the vocabulary of the spot check (`docs/adr/3004`): the URL scope, the pasted list,
 * the dates, the request, and the words of every state. Pure, so the page and the file say the same.
 *
 * **What is in the URL and what is not.** The URL carries the scope (CLAUDE.md §3.4): `?country=` (the
 * market), `?asOf=` (the stock day), `?salesFrom=` and `?salesTo=` (the sales window, both or neither) and
 * `?bySize=true`. The pasted list is NOT: the client checks hundreds of pairs at a time (573 in the workbook
 * that asked for this page, up to 2000 a request), several kilobytes that a link, a proxy and a server log
 * all truncate or refuse — the reason the platform's read is a POST. The list lives in the page and, so a
 * reload or Back keeps it, in this tab's session storage ({@link storedPairs}); a shared link opens the
 * scope with an empty list, and says so.
 *
 * **The as-of default** (asked by the owner with the page): with no `?asOf=` the page reads the latest
 * landed stock day of the market when it knows it — learnt from the platform's answer, `stockDay.lastLanded`
 * — and yesterday otherwise; the day is written into the URL with `replaceState` and said on the page, as a
 * run default is (ADR 9039). The sales window is never defaulted here: with none named the platform reads
 * the 7 days ending on the as-of day and says so (`salesWindow.basis`).
 */

import type { LookupPairModel, LookupRequest, LookupRowModel, LookupSizeModel } from '@/api/generated';

import { COUNTRY_PARAM } from '../programme';

import { HEALTH_PATH } from './reports';

export { COUNTRY_PARAM };

export const LOOKUP_PATH = `${HEALTH_PATH}/lookup`;

// ── the URL scope ────────────────────────────────────────────────────────────

/** The stock day read — the in-season chapter's own key, so a link that carries one keeps it. */
export const AS_OF_PARAM = 'asOf';
export const SALES_FROM_PARAM = 'salesFrom';
export const SALES_TO_PARAM = 'salesTo';
/** `true`: each pair carries its sizes (`1` is read the same). */
export const BY_SIZE_PARAM = 'bySize';

/** The contract's bounds: at most 2000 pairs a request, a sales window of at most 366 days. */
export const MAX_PAIRS = 2000;
export const MAX_SALES_DAYS = 366;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export interface LookupScope {
  readonly country: string | null;
  /** `null`: the URL names none, or a malformed one. */
  readonly asOf: string | null;
  /** Both or neither; `null` when the URL names neither, or a window the platform would refuse. */
  readonly sales: { readonly from: string; readonly to: string } | null;
  /** Why a sales window in the URL was not used, or `null`. */
  readonly salesProblem: SalesProblem | null;
  readonly bySize: boolean;
}

export type SalesProblem = 'half' | 'reversed' | 'too_long';

function isDay(value: string | null): value is string {
  return value !== null && ISO_DAY.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

function read(params: URLSearchParams, key: string): string | null {
  const value = params.get(key)?.trim() ?? '';
  return value === '' ? null : value;
}

/** Days from `from` to `to`, both counted. Pure. */
export function daysInWindow(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

/** A sales window the platform would take, or why not. Pure. */
export function checkSalesWindow(from: string | null, to: string | null): { readonly window: { from: string; to: string } | null; readonly problem: SalesProblem | null } {
  const hasFrom = isDay(from);
  const hasTo = isDay(to);
  if (!hasFrom && !hasTo) return { window: null, problem: null };
  if (!hasFrom || !hasTo) return { window: null, problem: 'half' };
  if (from > to) return { window: null, problem: 'reversed' };
  if (daysInWindow(from, to) > MAX_SALES_DAYS) return { window: null, problem: 'too_long' };
  return { window: { from, to }, problem: null };
}

/** The scope a search string states. Pure: no default here — the page writes the as-of default. */
export function readLookupScope(search: string): LookupScope {
  const params = new URLSearchParams(search);
  const asOf = read(params, AS_OF_PARAM);
  const sales = checkSalesWindow(read(params, SALES_FROM_PARAM), read(params, SALES_TO_PARAM));
  const bySize = read(params, BY_SIZE_PARAM);
  return {
    country: read(params, COUNTRY_PARAM),
    asOf: isDay(asOf) ? asOf : null,
    sales: sales.window,
    salesProblem: sales.problem,
    bySize: bySize === '1' || bySize === 'true',
  };
}

/** Yesterday, as the planner's calendar says it, `YYYY-MM-DD`. Pure over `now`. */
export function yesterday(now: Date): string {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

/** How the as-of day was chosen: named in the URL, the latest landed day, or yesterday. */
export type AsOfBasis = 'named' | 'latest_landed' | 'yesterday';

/**
 * The latest landed stock day of each market this tab has been told of — learnt from the platform's
 * answers, so a second lookup in the same market opens on it at once. Memory only: never across visits.
 */
const LAST_LANDED = new Map<string, string>();

export function rememberLastLanded(market: string, day: string | null): void {
  if (day !== null && isDay(day)) LAST_LANDED.set(market, day);
}

export function knownLastLanded(market: string): string | null {
  return LAST_LANDED.get(market) ?? null;
}

/** Forget what was learnt — for the tests. */
export function forgetLastLanded(): void {
  LAST_LANDED.clear();
}

/** The default as-of day for a market: the latest landed one when known, else yesterday. Pure but for the memory. */
export function defaultAsOf(market: string, now: Date): { readonly day: string; readonly basis: Exclude<AsOfBasis, 'named'> } {
  const known = knownLastLanded(market);
  return known === null ? { day: yesterday(now), basis: 'yesterday' } : { day: known, basis: 'latest_landed' };
}

// ── the pasted list ──────────────────────────────────────────────────────────

/** A header cell copied with the columns: store / article in the three languages and the client's own words. */
const HEADER_WORD = /^(stores?|tiendas?|negozi?o?|punto( de venta)?|pdv|shop|doors?|almac[eé]n|store_key|article|articles|art[ií]culos?|articol[oi]|material|sku|c[oó]digo|codice|article_key)$/i;

export interface ParsedPairs {
  /** Distinct pairs, in the order first pasted. */
  readonly pairs: readonly LookupPairModel[];
  /** Lines that repeated a pair already listed. */
  readonly duplicates: number;
  /** Lines with fewer than two keys, by their line number (1-based), as pasted. */
  readonly rejected: readonly { readonly line: number; readonly text: string }[];
  /** Whether the first line was read as a header and skipped. */
  readonly header: boolean;
}

/** One cell as pasted: trimmed, its spreadsheet quotes removed. */
function cell(text: string): string {
  const trimmed = text.trim();
  return trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"') ? trimmed.slice(1, -1).trim() : trimmed;
}

/** One line's cells: split on tabs, semicolons or commas where it has any, else on spaces. */
function cells(line: string): readonly string[] {
  const parts = /[\t;,]/.test(line) ? line.split(/[\t;,]/) : line.trim().split(/\s+/);
  return parts.map(cell).filter((part) => part !== '');
}

/**
 * Store × article pairs from text pasted out of a spreadsheet — two columns, store then article, divided
 * by tabs (Excel's copy), semicolons, commas or spaces; a header row is skipped; repeated pairs are kept
 * once, in first order; a line with one key is listed back, never guessed. Extra columns are ignored. Pure.
 */
export function parsePairs(text: string): ParsedPairs {
  const lines = text.split(/\r\n|\n|\r/);
  const seen = new Set<string>();
  const pairs: LookupPairModel[] = [];
  const rejected: { line: number; text: string }[] = [];
  let duplicates = 0;
  let header = false;
  let first = true;
  lines.forEach((raw, index) => {
    if (raw.trim() === '') return;
    const parts = cells(raw);
    if (first) {
      first = false;
      if (parts.some((part) => HEADER_WORD.test(part))) {
        header = true;
        return;
      }
    }
    const [storeKey, articleKey] = parts;
    if (storeKey === undefined || articleKey === undefined) {
      rejected.push({ line: index + 1, text: raw.trim() });
      return;
    }
    const key = `${storeKey}\u0000${articleKey}`;
    if (seen.has(key)) {
      duplicates += 1;
      return;
    }
    seen.add(key);
    pairs.push({ storeKey, articleKey });
  });
  return { pairs, duplicates, rejected, header };
}

/** The pairs as text again, one per line, tab-divided — what the box shows after a link restored them. Pure. */
export function pairsText(pairs: readonly LookupPairModel[]): string {
  return pairs.map((pair) => `${pair.storeKey}\t${pair.articleKey}`).join('\n');
}

const STORAGE_KEY = 'bata.health.lookup.pairs';

/** The list this tab last looked up, or `''` — session storage, which may be absent or refuse. */
export function storedPairs(): string {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function storePairs(text: string): void {
  try {
    if (text.trim() === '') window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, text);
  } catch {
    // A private window or a full store: the list lives in the page alone, which still works.
  }
}

// ── the request ──────────────────────────────────────────────────────────────

/**
 * The body of one lookup, or `null` while it cannot be asked: no market, no as-of day, no pair, too many
 * pairs, or a sales window the platform would refuse (the page says which). Pure.
 */
export function lookupBody(scope: LookupScope, asOf: string | null, pairs: readonly LookupPairModel[]): LookupRequest | null {
  if (scope.country === null || asOf === null || pairs.length === 0 || pairs.length > MAX_PAIRS || scope.salesProblem !== null) return null;
  return {
    market: scope.country,
    asOf,
    ...(scope.sales === null ? {} : { salesFrom: scope.sales.from, salesTo: scope.sales.to }),
    bySize: scope.bySize,
    pairs,
  };
}

// ── words ────────────────────────────────────────────────────────────────────

/** Each stock state in words; an unknown one keeps its code as `{code}`. */
export const STOCK_STATE_KEY: Readonly<Record<string, string>> = {
  reported: 'strategic.health.lookup.state.reported',
  no_row: 'strategic.health.lookup.state.noRow',
  day_not_landed: 'strategic.health.lookup.state.dayNotLanded',
};

export const UNKNOWN_STOCK_STATE_KEY = 'strategic.health.lookup.state.unknown';

export function stockStateKey(state: string): string {
  return STOCK_STATE_KEY[state] ?? UNKNOWN_STOCK_STATE_KEY;
}

/**
 * Why a stock figure of a row is absent, in words — never a 0: the pair has no row on a landed day, the day
 * has not landed, or the source did not report it. `null` when the figure is present. Pure.
 */
export function stockAbsenceKey(row: Pick<LookupRowModel, 'stockState'>, value: number | null): string | null {
  if (value !== null) return null;
  if (row.stockState === 'no_row') return 'strategic.health.lookup.absent.noRow';
  if (row.stockState === 'day_not_landed') return 'strategic.health.lookup.absent.dayNotLanded';
  return 'strategic.health.lookup.absent.notReported';
}

/**
 * A pair's or a size's on hand as the platform reports it: `onHandUnits` counts negative positions as zero
 * (ruling of 2026-10-03) — a pair whose only rows are negative reads 0 — and `negativeOnHandUnits` is the sum
 * of the negative rows (< 0), `null` when there are none (bata-platform ADR 9042 amended). A pair's figures are
 * sums over its sizes. The generated models carry both on rows and sizes alike.
 */
export type OnHandEntry = Pick<LookupSizeModel, 'onHandUnits' | 'negativeOnHandUnits'>;

/** The negative on-hand reported beside the counted figure, or `null` when there is none. Pure. */
export function negativeOnHand(entry: OnHandEntry): number | null {
  const value = entry.negativeOnHandUnits;
  return value === undefined || value === null ? null : value;
}

/** The signed stock — counted plus negatives — or `null` when there is no negative to show. Pure. */
export function signedOnHand(counted: number | null, negative: number | null): number | null {
  return counted === null || negative === null ? null : counted + negative;
}

/** Why a sales figure is absent: nothing landed for the window. `null` when present. Pure. */
export function salesAbsenceKey(value: number | null): string | null {
  return value === null ? 'strategic.health.lookup.absent.salesNotLanded' : null;
}

/**
 * Why a pair's net value is absent: nothing landed for the window, or sales in more than one currency (the
 * platform does not add them). `null` when present. Pure.
 */
export function netValueAbsenceKey(row: Pick<LookupRowModel, 'soldUnits' | 'netValue'>): string | null {
  if (row.netValue !== null) return null;
  return row.soldUnits === null ? 'strategic.health.lookup.absent.salesNotLanded' : 'strategic.health.lookup.absent.mixedCurrencies';
}

/** The words of the sales window's basis. */
export function salesBasisKey(basis: string): string {
  return basis === 'default_7_days_to_as_of' ? 'strategic.health.lookup.window.default' : 'strategic.health.lookup.window.named';
}

/** The words of how the as-of day was chosen. */
export const AS_OF_BASIS_KEY: Readonly<Record<AsOfBasis, string>> = {
  named: 'strategic.health.lookup.asOf.named',
  latest_landed: 'strategic.health.lookup.asOf.latestLanded',
  yesterday: 'strategic.health.lookup.asOf.yesterday',
};

/** The words of a sales window the URL named but the page did not use. */
export const SALES_PROBLEM_KEY: Readonly<Record<SalesProblem, string>> = {
  half: 'strategic.health.lookup.window.half',
  reversed: 'strategic.health.lookup.window.reversed',
  too_long: 'strategic.health.lookup.window.tooLong',
};

/** How many rows the sales figures do not fully cover. Pure. */
export function uncoveredRows(rows: readonly Pick<LookupRowModel, 'salesCovered'>[]): number {
  return rows.filter((row) => !row.salesCovered).length;
}
